import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Table,
  Button,
  Space,
  Tooltip,
  PageHeader,
  message,
  Modal,
  Popconfirm,
  Input,
} from 'antd';
import {
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  EyeOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import { MyBreadcrumb } from 'utils';
import {
  rackDeviceListService,
  rackDeviceDeleteService,
  rackDeviceSearchService,
} from 'services/rackdevice';
import NetworkToolWrapper from '../../layouts/NetworkToolWrapper';
import { accessControls } from 'utils';
import { debounce } from 'lodash';

const { Search } = Input;

export default function RackDeviceList() {
  const history = useHistory();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [permissions, setPermissions] = useState([]);

  const loadData = useCallback(() => {
    setLoading(true);
    rackDeviceListService()
      .then((response) => {
        setData(response.data.items || []);
      })
      .catch((error) => {
        message.error('Failed to load rack devices');
        console.error(error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const searchRecord = useCallback(
    (q) => {
      const query = (q || '').trim();
      if (!query) {
        loadData();
        return;
      }

      setLoading(true);
      rackDeviceSearchService(query)
        .then((response) => {
          setData(response.data.items || []);
        })
        .catch((error) => {
          message.error('Failed to search rack devices');
          console.error(error);
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [loadData],
  );

  const delayedQuery = useMemo(
    () => debounce((q) => searchRecord(q), 500),
    [searchRecord],
  );

  const onChange = useCallback(
    (e) => {
      delayedQuery(e.target.value);
    },
    [delayedQuery],
  );

  const deleteRecord = (id) => {
    rackDeviceDeleteService(id)
      .then(() => {
        message.success('Rack device deleted successfully');
        loadData();
      })
      .catch((error) => {
        message.error('Failed to delete rack device');
        console.error(error);
      });
  };

  const columns = [
    {
      title: 'Equipment Name',
      dataIndex: 'equipment_name',
      key: 'equipment_name',
      sorter: (a, b) => a.equipment_name.localeCompare(b.equipment_name),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      sorter: (a, b) => a.status.localeCompare(b.status),
    },
    {
      title: 'Primary Use',
      dataIndex: 'primary_use',
      key: 'primary_use',
      sorter: (a, b) => a.primary_use.localeCompare(b.primary_use),
    },
    {
      title: 'Equipment Alert',
      dataIndex: 'equipment_alert',
      key: 'equipment_alert',
      sorter: (a, b) =>
        (a.equipment_alert || '').localeCompare(b.equipment_alert || ''),
      render: (text) => text || 'N/A',
    },
    {
      title: 'Action',
      key: 'action',
      render: (text, record) => (
        <Space size="middle">
          {accessControls.can(
            permissions,
            'NetworkingToolRackDevice.read:any',
          ) && (
            <Tooltip title="View">
              <Button
                onClick={() => history.push(`/rackdevice/view/${record._id}`)}
                type="primary"
                shape="circle"
                icon={<EyeOutlined />}
              />
            </Tooltip>
          )}
          {accessControls.can(
            permissions,
            'NetworkingToolRackDevice.update:any',
          ) && (
            <Tooltip title="Edit">
              <Button
                onClick={() => history.push(`/rackdevice/update/${record._id}`)}
                type="primary"
                shape="circle"
                icon={<EditOutlined />}
              />
            </Tooltip>
          )}
          {accessControls.can(
            permissions,
            'NetworkingToolRackDevice.read:any',
          ) && (
            <Tooltip title="Upload Photos">
              <Button
                onClick={() => history.push(`/rackdevice/upload/${record._id}`)}
                type="primary"
                shape="circle"
                icon={<UploadOutlined />}
              />
            </Tooltip>
          )}
          {accessControls.can(
            permissions,
            'NetworkingToolRackDevice.delete:any',
          ) && (
            <Popconfirm
              title="Are you sure you want to delete this rack device?"
              description="This action cannot be undone."
              onConfirm={() => deleteRecord(record._id)}
              okText="Yes"
              okType="danger"
              cancelText="No"
            >
              <Tooltip title="Delete">
                <Button danger shape="circle" icon={<DeleteOutlined />} />
              </Tooltip>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  useEffect(() => {
    loadData();
    loadPermissions();
  }, [loadData]);

  const loadPermissions = () => {
    accessControls.fetch().then((permissions) => {
      setPermissions(permissions);
    });
  };

  return (
    <NetworkToolWrapper isBranchClientRequired={false}>
      <MyBreadcrumb
        items={[
          { label: 'Home', path: '/' },
          { label: 'Networking Dashboard', path: '/network-dashboard' },
          { label: 'Rack Devices', path: '' },
        ]}
      />
      <PageHeader
        ghost={false}
        onBack={() => window.history.back()}
        title="Rack Devices"
        subTitle={
          <Search
            allowClear={true}
            onChange={onChange}
            loading={loading}
            placeholder="Filter search result"
            onSearch={(value) => searchRecord(value)}
            style={{ width: 200 }}
          />
        }
        extra={[
          accessControls.can(
            permissions,
            'NetworkingToolRackDevice.create:any',
          ) && (
            <Button
              key="create"
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => history.push('/rackdevice/create')}
            >
              Create Rack Device
            </Button>
          ),
        ]}
      >
        <Table
          columns={columns}
          dataSource={data}
          rowKey="_id"
          loading={loading}
          pagination={{
            defaultPageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} items`,
          }}
        />
      </PageHeader>
    </NetworkToolWrapper>
  );
}
